import { Image, ImageProps } from '@graphcommerce/image'
import { ProductListItemFragment } from '@graphcommerce/magento-product/Api/ProductListItem.gql'
import { ProductListPrice } from '@graphcommerce/magento-product/components/ProductListPrice/ProductListPrice'
import { useProductLink } from '@graphcommerce/magento-product/hooks/useProductLink'
import { extendableComponent } from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import { Box, styled, useEventCallback } from '@mui/material'
import PageLink from 'next/link'
import React from 'react'
import { decodeHtml, formatDaysToDeliveryDisplay } from '../../../hooks/displayTools'
import { JsonLd } from '../../NextUi/JsonLd/JsonLd'
import { DeliveryCalendarPopover } from '../DeliveryCalendarPopover/DeliveryCalendarPopover'
import { jsonLdProduct, jsonLdProductSingleOffer } from '../JsonLdProduct/jsonLdProduct'

const { classes, selectors } = extendableComponent('ProductListItem', [
  'root',
  'item',
  'title',
  'titleContainer',
  'subtitle',
  'price',
  'overlayItems',
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
  'imageContainer',
  'placeholder',
  'image',
  'discount',
] as const)

export type OverlayAreaKeys = 'topLeft' | 'bottomLeft' | 'topRight' | 'bottomRight'

export type OverlayAreas = Partial<Record<OverlayAreaKeys, React.ReactNode>>

type StyleProps = {
  imageOnly?: boolean
}

type BaseProps = { children?: React.ReactNode } & StyleProps &
  OverlayAreas &
  ProductListItemFragment &
  Pick<ImageProps, 'loading' | 'sizes' | 'dontReportWronglySizedImages'>

export type ProductListItemProps = BaseProps & {
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>, item: ProductListItemFragment) => void
} & { isHelpListItem?: boolean }

export function ProductListItem(props: ProductListItemProps) {
  const {
    uid,
    small_image,
    name,
    children,
    imageOnly = false,
    onClick,
    production_time,
    moq,
    min_tier_price,
    free_shipping,
    isHelpListItem,
  } = props

  const handleClick = useEventCallback((e: React.MouseEvent<HTMLAnchorElement>) =>
    onClick?.(e, props),
  )

  const handleBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e as unknown as React.MouseEvent<HTMLAnchorElement>, props)
  }

  // Replicate the product page's initial variant selection: find the variant whose attributes
  // match the first value uid of each configurable option (same as traverseAttrTree with no selection).
  type ConfigurableListProps = {
    configurable_options?: Array<{
      values?: Array<{ uid?: string | null } | null> | null
    } | null> | null
    variants?: Array<{
      attributes?: Array<{ uid?: string | null } | null> | null
      product?: {
        price_tiers?: Array<{
          quantity?: number | null
          final_price?: { value?: number | null } | null
        } | null> | null
      } | null
    } | null> | null
  }
  const { configurable_options, variants: configVariants } =
    props as unknown as ConfigurableListProps
  const firstOptionUids = (configurable_options ?? [])
    .map((opt) => opt?.values?.[0]?.uid)
    .filter((optUid): optUid is string => optUid != null)
  const defaultVariant = configVariants?.find((v) =>
    firstOptionUids.every((optUid) => v?.attributes?.some((attr) => attr?.uid === optUid)),
  )
  const activeTier = defaultVariant?.product?.price_tiers?.[0]
  const displayQty = activeTier?.quantity ?? moq
  const displayUnitPrice = activeTier?.final_price?.value

  let minimumOrderAndPriceDisplay: string | null = null
  if (displayQty && displayUnitPrice) {
    const decimals = displayUnitPrice >= 1 ? 2 : 3
    const roundedUnit = parseFloat(displayUnitPrice.toFixed(decimals))
    const total = Math.round(displayQty * roundedUnit).toLocaleString('en-US')
    minimumOrderAndPriceDisplay = `${displayQty.toLocaleString()} min. / $${total}`
  } else if (moq && min_tier_price) {
    minimumOrderAndPriceDisplay = `${moq.toLocaleString()} min. / $${Math.round(min_tier_price).toLocaleString('en-US')}`
  }

  const productLink = useProductLink(props)

  return (
    <div
      className={`min-h-[310px] rounded-[5px] ${isHelpListItem ? 'h-full w-full md:w-[110%]' : ''}`}
    >
      {isHelpListItem ? (
        <button
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          onClick={handleBtnClick}
          type='button'
          className='relative flex h-[95%] w-full items-center justify-start md:w-[105%]'
        >
          <Image
            src={`${small_image?.url ?? ''}`}
            unoptimized
            layout='fill'
            className='h-full w-auto object-cover'
            sx={{
              aspectRatio: '2/3',
              borderRadius: '24px',
            }}
          />
          <span className='Type-XL-Bold md:Type-XXL-Bold absolute  left-4 top-5 w-fit pr-2 text-left text-[18px] md:text-[20px]'>
            Trouble finding the right <span className='italic'>match</span>?
          </span>
          <span className='Type-XL-Bold absolute bottom-5 left-4 flex flex-col text-left'>
            <span>
              Click <span className='underline'>here</span>
            </span>
            <span className='font-normal'>We'll help you out!</span>
          </span>
        </button>
      ) : (
        <PageLink
          href={productLink}
          passHref
          onClick={onClick ? handleClick : undefined}
          onKeyDown={() => (onClick ? handleClick : undefined)}
          className='block'
          role='button'
          tabIndex={0}
        >
          <div className='flex h-full w-full items-center justify-center'>
            <JsonLd
              item={{
                '@context': 'https://schema.org',
                ...jsonLdProduct(props),
                ...jsonLdProductSingleOffer(props),
              }}
              identifier={uid}
            />
            {small_image ? (
              <div className='flex h-full w-full items-center justify-center'>
                <div className='relative flex w-full items-center justify-center rounded-[6px]'>
                  <div className='absolute top-2 flex w-full gap-[6px] px-2'>
                    {/* <div className='flex h-[20px] w-auto items-center justify-start rounded-[20px] bg-[white] px-1 py-[1px] text-center text-new-templi-purple'>
                    <span className='truncate whitespace-nowrap px-1 text-[9px] font-semibold'>
                      Best Seller
                    </span>
                  </div> */}
                    {free_shipping && (
                      <div className='flex h-[20px] w-auto items-center justify-start rounded-[20px] bg-[white] px-1 py-[1px] text-center text-[#0d6216]'>
                        <span className='truncate whitespace-nowrap px-1 text-[9px] font-semibold '>
                          Free Ship
                        </span>
                      </div>
                    )}
                  </div>

                  <Box
                    component='a'
                    className=''
                    sx={{
                      overflow: 'hidden',
                      height: '100%',
                      width: '100%',
                      backgroundRepeat: 'no-repeat',
                      aspectRatio: '5/6',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderRadius: '6px',
                      '@media (min-width: 960px)': {
                        minHeight: '200px',
                      },
                    }}
                  >
                    <Image
                      src={`${small_image.url ?? ''}`}
                      unoptimized
                      layout='fill'
                      className='h-full w-auto object-cover'
                      sx={{
                        aspectRatio: '5/6',
                        borderRadius: '6px',
                      }}
                    />
                  </Box>
                </div>
              </div>
            ) : (
              <Box
                sx={{
                  gridArea: `1 / 1 / 4 / 4`,
                  typography: 'caption',
                  display: 'flex',
                  textAlign: 'center',
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'background.default',
                  userSelect: 'none',
                  borderRadius: '6px',
                  aspectRatio: '5/6',
                }}
                className={`${classes.placeholder} ${classes.image}`}
              >
                <Trans id='No Image' />
              </Box>
            )}
          </div>

          {!imageOnly && (
            <div className='flex flex-col items-start justify-center gap-2.25 pt-2.25 pb-6.5'>
              <h3
                className='w-full'
                style={{ fontWeight: 500, fontSize: 16, lineHeight: '19px', color: '#0F0F10' }}
              >
                {decodeHtml(name ?? '')}
              </h3>

              {production_time && (
                <div className='flex items-center gap-1'>
                  <span
                    style={{ fontWeight: 400, fontSize: 16, lineHeight: '19px', color: '#757575' }}
                  >
                    Est delivery by{' '}
                    <DeliveryCalendarPopover production_time={Number(production_time)}>
                      <span
                        style={{ color: '#0F0F10', textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {formatDaysToDeliveryDisplay(production_time)}
                      </span>
                    </DeliveryCalendarPopover>
                  </span>
                </div>
              )}

              {children}

              {minimumOrderAndPriceDisplay && (
                <span
                  style={{ fontWeight: 400, fontSize: 16, lineHeight: '19px', color: '#757575' }}
                >
                  {minimumOrderAndPriceDisplay}
                </span>
              )}
            </div>
          )}
        </PageLink>
      )}
    </div>
  )
}

ProductListItem.selectors = { ...selectors, ...ProductListPrice.selectors }
